import { useMemo, useState } from 'react';
import type { ChartConfig } from '../types/dashboard';
import type { FilterSpec } from '../charts/model';
import { ChartRenderer } from './ChartRenderer';
import { getChart } from '../charts';
import { DEFAULT_ASPECT_RATIO } from '../charts/theme/layout';
import { Copy, Edit2, GripVertical, Trash2 } from 'lucide-react';

/**
 * The selected card's marker colour: the consuming app's primary token, which is
 * what ui-kit's Tree marks a selected ROW with — so a chart and its tree row carry
 * the same mark. The fallback keeps a host that ships no tokens on a visible
 * near-black rather than a transparent border.
 */
const SELECTION_COLOR = 'rgb(var(--color-primary, 30 30 30))';

interface ChartCardProps {
  config: ChartConfig;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Duplicate affordance in the header (consumer decides what "duplicate" means). */
  onDuplicate?: () => void;
  /**
   * Commit a change the card made to its own config — today, the level selector's
   * picked bench. Given a consumer that stores the patch (see DashboardRenderer,
   * which forwards it to `updateChart`), the pick becomes part of the chart config
   * and so reaches the report, the PNG export and the saved snapshot. Without it the
   * card still works, keeping the pick locally: that is the read-only viewer's case.
   */
  onConfigChange?: (patch: Partial<ChartConfig>) => void;
  /**
   * The name the HOST calls this chart, shown in the card header.
   *
   * The figure's own title is drawn inside the plot so it travels to the PNG and the
   * report (charts/shared/layout.ts), which leaves the card with nothing the app can
   * label it by. This fills that gap and is deliberately NOT part of `config`: it is
   * app chrome, so it must not reach a saved presentation, a snapshot or an export.
   * Rendering it does not require an affordance (see `hasHeader`): a read-only
   * card still gets a header row carrying just this label. The report is unaffected
   * either way — it renders figures through ReportFigure, not through this card.
   */
  label?: string;
  /** Highlight the card as the selected one (only meaningful with `onClick`). */
  selected?: boolean;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  isDragOver?: boolean;
}

export function ChartCard({
  config,
  onClick,
  onEdit,
  onDelete,
  onDuplicate,
  onConfigChange,
  label,
  selected = false,
  draggable = true,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragOver,
}: ChartCardProps) {
  const { title, statistics } = config;

  // Level selector (plan-view bench picker): when opted in via
  // `options.levelSelector`, enumerate the distinct values of the chart's first
  // categorical filter from the inline data and let the user switch which one is
  // shown. The selected value replaces that filter's value in the config handed
  // to the renderer (which re-applies filters on every render). Hooks run
  // unconditionally; the control only renders when there are values to pick.
  const isCategorical = (f: FilterSpec): f is Extract<FilterSpec, { kind: 'categorical' }> =>
    f.kind === 'categorical';
  const levelFilter = config.filters?.find(isCategorical);
  const levelFilterIdx = levelFilter ? config.filters!.indexOf(levelFilter) : -1;
  const levelValues = useMemo(() => {
    if (!config.options?.levelSelector || !levelFilter || !config.data) return [];
    const seen = new Set<string>();
    for (const row of config.data) {
      const v = row[levelFilter.field];
      if (v !== undefined && v !== null) seen.add(String(v));
    }
    // Numeric-descending so the top bench (highest elevation) leads.
    return [...seen].sort((a, b) => Number(b) - Number(a));
  }, [config.options?.levelSelector, config.data, levelFilter]);

  // The picked bench is a DRAFT over the config's own level, not a second source of
  // truth. It exists so dragging feels immediate — an <input type="range"> fires per
  // pixel, and writing each of those to the store re-renders every card and re-filters
  // the whole dataset — while the commit happens on release (see the slider below).
  //
  // Released whenever the config's level changes: once the commit lands the draft is
  // redundant, and after a re-run (chart ids are stable, so this instance survives) a
  // stale pick would filter the new grid down to nothing.
  // The draft records WHICH config level it was made against, so a config that moved
  // on (a landed commit, or a re-run) invalidates it during render — no effect, no
  // setState-in-effect, no window where the two disagree.
  const configLevel = levelFilter?.values?.[0];
  const [draft, setDraft] = useState<{ level: string; against: string | undefined } | null>(null);
  const draftLevel = draft && draft.against === configLevel ? draft.level : undefined;
  // A config level absent from the current data is stale in the same way — ignore it
  // rather than filtering everything away.
  const knownConfigLevel =
    configLevel !== undefined && levelValues.includes(configLevel) ? configLevel : undefined;
  const activeLevel = draftLevel ?? knownConfigLevel ?? levelValues[0];
  const setDraftLevel = (level: string) => setDraft({ level, against: configLevel });
  const hasLevelSelector = levelValues.length > 0 && levelFilter && activeLevel !== undefined;
  // Slider maps left→right = lowest→highest elevation, so mirror the (descending)
  // levelValues into an ascending array and locate the active bench in it.
  const ascLevels = useMemo(() => [...levelValues].reverse(), [levelValues]);
  const levelIndex = Math.max(0, ascLevels.indexOf(activeLevel ?? ''));

  // Write the drafted bench into the chart's own filters — the same shape the config
  // already carries, with every other filter passed through by index. A no-op when the
  // draft matches what the config already says, so releasing without moving the slider
  // (or a plain focus/blur) writes nothing.
  const commitLevel = () => {
    if (!onConfigChange || draftLevel === undefined || draftLevel === configLevel) return;
    onConfigChange({
      filters: config.filters!.map((f, i) =>
        i === levelFilterIdx ? { ...f, values: [draftLevel] } : f
      ),
    });
  };

  // Human-readable form of the shown level: an elevation with its unit, or the bare
  // value when the level is a unitless grid index.
  const levelReadout = config.options?.levelSelectorUnit
    ? `${Number(activeLevel).toLocaleString('es-CL')} ${config.options.levelSelectorUnit}`
    : String(activeLevel ?? '');

  // Rebind the level filter to the picked value before rendering; other filters
  // pass through untouched. When the selector is inactive, render config as-is.
  //
  // The per-bench readouts (subtitle "Banco z = … · n = … · Accuracy = …" and the
  // legend's "— N" counts) are NOT spliced in here: they are derived from the shown
  // rows by normalizeConfig, so the report and the PNG export — neither of which goes
  // through this card — carry them too. See charts/shared/levelView.
  const renderConfig: ChartConfig = hasLevelSelector
    ? {
        ...config,
        filters: config.filters!.map((f, i) =>
          i === levelFilterIdx ? { ...f, values: [activeLevel] } : f
        ),
      }
    : config;

  // The kit renders whatever statistics the consumer provides. Domain-specific
  // metrics (r2/rmse, error totals) are the consumer's job to compute and pass
  // via `config.statistics`; the kit stays generic.
  //
  // When `showStatsBox` is set the stats are drawn inside the figure (see
  // ChartRenderer's statsAnnotations), so the card footer would duplicate them —
  // and would render the figure's <br>-joined values as literal text. Suppress
  // the footer in that case; the in-figure box is the single source.
  const statsToRender = config.options?.showStatsBox ? [] : (statistics ?? []);

  // Title/subtitle are drawn inside the figure (Plotly) so they travel in PNG
  // exports — see charts/shared/layout.ts. The card no longer renders them.

  // Sizing priority: an explicit cardHeight pins an absolute height; otherwise
  // the card derives its height from its width via CSS `aspect-ratio` (height =
  // width / aspectRatio) so charts don't look flattened in wide cards. The ratio
  // resolves consumer → per-type default → global default — no per-type switch
  // here; the type's natural ratio is read from its registered definition.
  const cardHeight = config.options?.cardHeight;
  const aspectRatio =
    config.options?.aspectRatio ??
    getChart(config.type)?.defaultAspectRatio ??
    DEFAULT_ASPECT_RATIO;
  const chartStyle = cardHeight ? { height: `${cardHeight}px` } : { aspectRatio, width: '100%' };

  // A colSpan:2 card takes the full row on the 2-column grid (no-op on 1 column).
  const spanClass = config.options?.colSpan === 2 ? 'lg:col-span-2' : '';

  // The header hosts the drag handle + edit/duplicate/delete affordances, and the
  // host's label. `label` counts on its own: identity is not an affordance, and
  // tying it to one meant a card lost its name exactly where the name matters most
  // — a read-only mount, i.e. a dashboard whose presentation is finished and is
  // being read. With none of the four it is omitted, letting the plot fill the card.
  const hasHeader = Boolean(onEdit || onDelete || onDuplicate || draggable || label);

  // When `onClick` is given the whole card is an activation target. Expose it to
  // the keyboard (role + tabIndex + Enter/Space), not just the mouse. The inner
  // edit/delete buttons stopPropagation so they stay independent affordances.
  const interactive = Boolean(onClick);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      // The card's identity in the DOM: a host that needs to reach a specific chart
      // (conciliatron scrolls to the one picked in its sidebar tree) has no other
      // handle on it — the cards are created by the kit, so a ref map would have to
      // cross the module boundary. Data attribute over id: several dashboards can
      // share a page, and ids would collide.
      data-chart-id={config.id}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Abrir ${title}` : undefined}
      aria-pressed={interactive ? selected : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      // Only a DROP target here — the drag SOURCE is the header handle below, so
      // dragging a chart doesn't fight Plotly's own zoom/pan on the plot area.
      onDragOver={onDragOver}
      onDrop={onDrop}
      // Selected: the SAME mark the sidebar tree puts on the selected row — theme
      // primary, 2px inset bar (ui-kit's Tree). Clicking one to find the other is
      // the whole gesture, so the two surfaces must answer in one language; the old
      // blue ring named no token in the palette at all. Inline because the value is
      // a theme token, with a fallback for a host that ships no tokens; the resting
      // shadow is restated here since this overrides `shadow-sm`.
      style={
        selected
          ? {
              borderColor: SELECTION_COLOR,
              boxShadow: `inset 2px 0 0 ${SELECTION_COLOR}, 0 1px 2px rgb(0 0 0 / 0.05)`,
            }
          : undefined
      }
      className={`overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md ${selected ? '' : 'border-gray-200'} ${spanClass} ${interactive ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-600' : ''} ${isDragOver ? 'border-t-4 border-gray-500' : ''}`}
      onClick={onClick}
    >
      {/* Header — drag handle + host label (left) + edit/duplicate/delete (right);
          the FIGURE's title is drawn inside the plot. Omitted when there are no
          affordances, which is also what keeps the label out of the report. */}
      {hasHeader && (
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-2">
          {/* Drag handle — the ONLY drag source (dragging the plot would fight
              Plotly). stopPropagation so it doesn't trigger the card's onClick. */}
          <div className="flex min-w-0 items-center gap-1.5">
            {draggable && (
              <span
                draggable
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onClick={(e) => e.stopPropagation()}
                aria-label="Mover gráfico"
                title="Arrastra para reordenar"
                className="cursor-grab rounded p-1 text-gray-400 hover:text-gray-700 active:cursor-grabbing"
              >
                <GripVertical className="h-4 w-4" />
              </span>
            )}
            {/* An EYEBROW, not a second title: the figure's own title sits just below
                it inside the plot, and at the same size the two read as competing
                headings for one card. Stepped down (11px, semibold, muted) it reads
                as what it is — the name this chart goes by in the tree. NOT
                uppercased: the name carries column names straight from the user's
                file, and "CUT_CP VS CUT_LP" is not how they are spelled anywhere else.
                `truncate` over wrapping: the header is one row high, and growing it
                would shift every card's plot area. The full name stays reachable as
                the title attribute. */}
            {label && (
              <span
                className="truncate text-[11px] font-semibold tracking-[0.04em] text-gray-500"
                title={label}
              >
                {label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
          {onEdit && (
            <button
              type="button"
              aria-label="Editar widget"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="rounded p-1 text-gray-500 hover:text-gray-800"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          )}
          {onDuplicate && (
            <button
              type="button"
              aria-label="Duplicar widget"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              className="rounded p-1 text-gray-500 hover:text-gray-800"
            >
              <Copy className="h-4 w-4" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              aria-label="Eliminar widget"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              // Monochrome with its siblings: the red made delete the loudest
              // thing on a card whose subject is the chart.
              // NOTE: deletion is immediate — neither `removeChart` here nor
              // conciliatron's `onChartDelete` asks for confirmation — so this
              // removed the only visual warning the action carried. If that turns
              // out to bite, the fix is a confirm step, not repainting the icon.
              className="rounded p-1 text-gray-500 hover:text-gray-800"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          </div>
        </div>
      )}

      {/* Chart — with a vertical bench slider on the RIGHT when a level selector
          is active, so elevation reads as a Z axis beside the plan view without
          eating the plot's left margin. Slider spans the plot height; top =
          highest cota. stopPropagation so dragging doesn't activate the card. */}
      {hasLevelSelector ? (
        <div className="flex items-stretch">
          {/* `min-w-0` is load-bearing: a flex item defaults to `min-width: auto`, so
              with `width: 100%` from `chartStyle` the plot refuses to shrink and
              pushes the slider column past the card's edge — where `overflow-hidden`
              clips it away. Measured: the slider left the card below ~1500px of
              window width and simply vanished, which reads as "the map lost its
              bench selector". */}
          <div className="min-w-0 flex-1 p-4" style={chartStyle}>
            <ChartRenderer config={renderConfig} />
          </div>
          <div
            className="flex flex-col items-center gap-2 py-4 pr-3"
            onClick={(e) => e.stopPropagation()}
            // Keyboard too, not just the mouse: the card handles Enter/Space as its
            // own activation, so a Space aimed at the focused slider toggled the
            // card's selection on its way up.
            onKeyDown={(e) => e.stopPropagation()}
          >
            <span className="whitespace-nowrap text-xs font-medium tabular-nums text-gray-800">
              {levelReadout}
            </span>
            <input
              type="range"
              min={0}
              max={ascLevels.length - 1}
              step={1}
              value={levelIndex}
              onChange={(e) => setDraftLevel(ascLevels[Number(e.target.value)])}
              // Commit on release, not on every change: see the draft comment above.
              onPointerUp={commitLevel}
              onKeyUp={commitLevel}
              onBlur={commitLevel}
              aria-label={config.options?.levelSelectorLabel ?? 'Nivel'}
              // The value the range role reports is the slider's INDEX; without this a
              // screen reader would announce "3 of 12" for a bench at 3.007,5 m.
              aria-valuetext={levelReadout}
              style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
              className="flex-1 cursor-pointer accent-blue-600"
            />
            <span className="whitespace-nowrap text-center text-[10px] leading-tight text-gray-500">
              {config.options?.levelSelectorLabel ?? 'Nivel'}
              <br />({levelIndex + 1}/{ascLevels.length})
            </span>
          </div>
        </div>
      ) : (
        <div className="p-4" style={chartStyle}>
          <ChartRenderer config={renderConfig} />
        </div>
      )}

      {/* Statistics Footer */}
      {statsToRender && statsToRender.length > 0 && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-2">
          <div className="flex flex-wrap items-center gap-4">
            {statsToRender.map((stat, index) => (
              <div key={index} className="text-xs">
                <span className="text-gray-500">{stat.label}: </span>
                {/* Values are authored for the in-figure box, where Plotly reads
                    `<br>` as a line break. Rendered as-is here they showed up as
                    literal "<br>" text, so split on it and stack the lines. */}
                <span className="font-medium text-gray-900">
                  {String(stat.value)
                    .split('<br>')
                    .map((line, i, all) => (
                      <span key={i}>
                        {line}
                        {i < all.length - 1 && <br />}
                      </span>
                    ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
